import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

interface UseImportNavigationGuardProps {
  enabled: boolean;
}

interface UseImportNavigationGuardResult {
  isDialogOpen: boolean;
  onLeave: () => void;
  onStay: () => void;
}

export const useImportNavigationGuard = ({
  enabled,
}: UseImportNavigationGuardProps): UseImportNavigationGuardResult => {
  const blocker = useBlocker(
    useCallback(
      ({ historyAction }) => enabled && historyAction !== 'POP',
      [enabled]
    )
  );

  const isDialogOpen = blocker.state === 'blocked';

  const onLeave = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const onStay = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  // Browser refresh / tab close guard
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required for Chrome/Safari
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled]);

  return {
    isDialogOpen,
    onLeave,
    onStay,
  };
};
